import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Clock3, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const LIMITS = { minutes: 10080, hours: 168, days: 7 };

export default function TimeoutDialog({ open, onOpenChange, username, reportId, onDone }) {
  const [amount, setAmount] = useState("1");
  const [unit, setUnit] = useState("hours");
  const [reason, setReason] = useState("Public chat moderation");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setAmount("1");
      setUnit("hours");
      setReason("Public chat moderation");
    }
  }, [open]);

  const minutes = useMemo(() => {
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) return 0;
    if (unit === "days") return Math.round(value * 1440);
    if (unit === "hours") return Math.round(value * 60);
    return Math.round(value);
  }, [amount, unit]);

  const max = LIMITS[unit];
  const valid = Number(amount) >= 1 && Number(amount) <= max && minutes >= 1 && minutes <= 10080 && reason.trim().length >= 3;

  const submit = async () => {
    if (!valid) return;
    setSaving(true);
    try {
      await api.post("/admin/timeouts", { username, reason: reason.trim(), custom_minutes: minutes, report_id: reportId || undefined });
      toast.success(`@${username} timed out`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-3xl">
        <DialogTitle className="flex items-center gap-2 font-heading font-extrabold text-slate-900"><Clock3 className="h-5 w-5 text-purple-500" />Timeout @{username}</DialogTitle>
        <DialogDescription>Mute this member from public chat. They can still log in and use private messages. Maximum duration is 7 days.</DialogDescription>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-[1fr_140px] gap-3">
            <div className="space-y-2"><Label htmlFor="timeout-amount">Duration</Label><Input id="timeout-amount" type="number" min="1" max={max} step="1" value={amount} onChange={(e) => setAmount(e.target.value)} className="rounded-xl" /></div>
            <div className="space-y-2"><Label>Unit</Label><Select value={unit} onValueChange={setUnit}><SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="minutes">Minutes</SelectItem><SelectItem value="hours">Hours</SelectItem><SelectItem value="days">Days</SelectItem></SelectContent></Select></div>
          </div>
          <p className="text-xs text-slate-400">Maximum: {unit === "days" ? "7 days" : unit === "hours" ? "168 hours" : "10080 minutes"}.</p>
          <div className="space-y-2"><Label htmlFor="timeout-reason">Reason</Label><Textarea id="timeout-reason" value={reason} onChange={(e) => setReason(e.target.value.slice(0, 500))} maxLength={500} className="min-h-24 rounded-xl" placeholder="Why are you timing them out?" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button><Button onClick={submit} disabled={!valid || saving} className="rounded-full bg-purple-500 text-white hover:bg-purple-600">{saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Clock3 className="mr-1.5 h-4 w-4" />}Timeout</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
